import React, { useLayoutEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

type Props = {
  /** Optional: explicitly set your scroll container selector if you know it */
  containerSelector?: string;
};

const ScrollToTop: React.FC<Props> = ({ containerSelector }) => {
  const { pathname, hash } = useLocation();
  const firstLoadRef = useRef(true);

  // 1) Kill native scroll restoration so our logic wins
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Utility: find the element that actually scrolls
  const findScrollElement = useCallback((): HTMLElement => {
    // a) explicit container, if provided
    if (containerSelector) {
      const el = document.querySelector(containerSelector) as HTMLElement | null;
      if (el) return el;
    }

    // b) common app containers (auto-detect first scrollable with overflow + overflowed content)
    const candidates = [
      "[data-scroll-container]",
      "main",
      ".main-content",
      ".content",
      "#content",
      "#app",
      "#root",
      "body",
      "html",
    ];

    for (const sel of candidates) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el && canScroll(el)) return el;
    }

    // c) sensible fallback
    return (document.scrollingElement || document.documentElement) as HTMLElement;
  }, [containerSelector]);

  const canScroll = (el: HTMLElement) => {
    const style = getComputedStyle(el);
    const overflowY = style.overflowY;
    const scrollable =
      overflowY === "auto" || overflowY === "scroll" || el === document.documentElement || el === document.body;

    return scrollable && el.scrollHeight > el.clientHeight;
  };

  const scrollTop = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = findScrollElement();
    // Try the target container
    el.scrollTo({ top: 0, left: 0, behavior });
    // Also force window/document just in case (Safari quirks / nested layouts)
    window.scrollTo({ top: 0, left: 0, behavior });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [findScrollElement]);

  const scrollToHash = useCallback((h: string) => {
    const raw = h.replace("#", "");
    const id = decodeURIComponent(raw);

    let attempts = 0;
    let done = false;

    const tryScroll = () => {
      if (done) return;
      attempts += 1;

      const target =
        document.getElementById(id) ||
        document.querySelector(`[name="${CSS?.escape ? CSS.escape(id) : id}"]`);

      if (target) {
        (target as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
        done = true;
        return;
      }

      if (attempts < 16) {
        // Wait for mounts (images/lazy components). 16 rAF ≈ one second.
        requestAnimationFrame(tryScroll);
      } else {
        // Fallback: go top if never found
        scrollTop();
      }
    };

    requestAnimationFrame(tryScroll);
  }, [scrollTop]);

  useLayoutEffect(() => {
    // Hash present: scroll to element (even on same-path hash changes)
    if (hash) {
      scrollToHash(hash);
      return;
    }

    // First render after load: let the browser keep its initial pos
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      return;
    }

    // Route changed without hash: go to top (after paint to avoid flicker)
    requestAnimationFrame(() => scrollTop());
  }, [pathname, hash, containerSelector, scrollToHash, scrollTop]);

  return null;
};

export default ScrollToTop;
